/**
 * AbilityTestRig — Panel 3 in the ability-debug family.
 *
 * Modernized tabbed active driver panel for abilities:
 *   - Cast Bench: browsable, searchable ability cards with genuine icons & one-click cast
 *   - Overrides: dev bypass flags (infinite mana, no CD, no GCD, no range gate)
 *   - Hotkeys: numpad 1-9 slot bindings with real-time test firing
 *   - Macros: record, save, play, and loop ability sequences
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGame } from '../contexts/GameContext';
import { abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { NetworkManager } from '../../network/NetworkManager';
import {
  useRotationPlayer,
  loadRotations,
  saveRotations,
  type RotationDefinition,
  type RotationStep,
} from '../hooks/useRotationPlayer';
import {
  loadHotkeys,
  saveHotkeys,
  type HotkeyBindings,
} from '../../systems/input/abilityTestHotkeys';
import { TabBar, type TabDescriptor } from '../primitives';
import { type OverrideFlags, type OverridablePlayer } from './AbilityTestRig/shared';
import { rigStyles as styles } from './AbilityTestRig/abilityRigStyles';
import { OverridePanel } from './AbilityTestRig/OverridePanel';
import { HotkeySlotsPanel } from './AbilityTestRig/HotkeySlotsPanel';
import { CastBenchPanel } from './AbilityTestRig/CastBenchPanel';
import { RotationMacrosPanel } from './AbilityTestRig/RotationMacrosPanel';

const SEED_ROTATIONS: RotationDefinition[] = [
  {
    id: 'mage-basic-combo',
    name: 'Mage basic combo',
    steps: [
      { abilityId: 'fireball', delayMs: 0 },
      { abilityId: 'frost_bolt', delayMs: 1500 },
      { abilityId: 'mage_auto', delayMs: 1500 },
      { abilityId: 'mage_auto', delayMs: 700 },
      { abilityId: 'mage_auto', delayMs: 700 },
    ],
  },
];

function readOverrides(player: OverridablePlayer | null): OverrideFlags {
  return {
    infiniteMana: !!player?.infiniteMana,
    noCooldowns: !!player?.noCooldowns,
    noGCD: player?.gcdOverride === 0,
    noRangeGate: !!player?.noRangeGate,
  };
}

function applyOverrideLocal(player: OverridablePlayer | null, key: keyof OverrideFlags, value: boolean) {
  if (!player) return;
  switch (key) {
    case 'infiniteMana': player.infiniteMana = value; break;
    case 'noCooldowns': player.noCooldowns = value; break;
    case 'noGCD': player.gcdOverride = value ? 0 : -1; break;
    case 'noRangeGate': player.noRangeGate = value; break;
  }
}

function sendChatCommand(cmd: string): void {
  try {
    const nm = (window as unknown as { nm?: NetworkManager }).nm;
    const room = nm?.getRoom?.();
    if (!room) return;
    room.send('chatMessage', { message: cmd });
  } catch {
    // best-effort
  }
}

function sumStepDelays(steps: RotationStep[]): number {
  return steps.reduce((acc, s) => acc + s.delayMs, 0);
}

export const AbilityTestRigContent: React.FC = () => {
  const { player } = useGame();
  const [activeTab, setActiveTab] = useState<'bench' | 'overrides' | 'hotkeys' | 'rotations'>('bench');

  const localPlayer = useMemo<OverridablePlayer | null>(() => {
    try {
      const reg = ServiceRegistry.getInstance();
      const es = reg.get('EntitySystem', { optional: true });
      return (es?.getPlayerManager?.()?.getPlayer?.() ?? null) as OverridablePlayer | null;
    } catch {
      return null;
    }
  }, [player?.id]);

  const allAbilities = useMemo(
    () => abilityRegistry.getAll().sort((a, b) => a.id.localeCompare(b.id)),
    [],
  );

  const [filter, setFilter] = useState('');
  const [overrides, setOverrides] = useState<OverrideFlags>(() => readOverrides(localPlayer));
  const [lastCastId, setLastCastId] = useState<string | null>(null);
  const [lastCastResult, setLastCastResult] = useState<'success' | 'pending' | null>(null);
  const [rotations, setRotations] = useState<RotationDefinition[]>(() => {
    const stored = loadRotations();
    return stored.length > 0 ? stored : SEED_ROTATIONS;
  });
  const [activeRotationId, setActiveRotationId] = useState<string | null>(rotations[0]?.id ?? null);
  const [recording, setRecording] = useState<{ name: string; steps: RotationStep[]; startedAt: number } | null>(null);
  const [hotkeys, setHotkeys] = useState<HotkeyBindings>(() => loadHotkeys());

  useEffect(() => {
    const id = setInterval(() => {
      setOverrides(readOverrides(localPlayer));
    }, 500);
    return () => clearInterval(id);
  }, [localPlayer]);

  useEffect(() => {
    saveRotations(rotations);
  }, [rotations]);

  useEffect(() => {
    saveHotkeys(hotkeys);
  }, [hotkeys]);

  const cast = useCallback(
    (abilityId: string) => {
      const reg = ServiceRegistry.getInstance();
      const es = reg.get('EntitySystem', { optional: true });
      es?.getPlayerManager?.()?.performAbility(abilityId);
      setLastCastId(abilityId);
      setLastCastResult('pending');
      setTimeout(() => setLastCastResult('success'), 2000);

      if (recording) {
        const now = Date.now();
        const lastStep = recording.steps[recording.steps.length - 1];
        const delayMs = lastStep ? now - recording.startedAt - sumStepDelays(recording.steps) : 0;
        setRecording({
          ...recording,
          steps: [...recording.steps, { abilityId, delayMs }],
        });
      }
    },
    [recording],
  );

  const rotationPlayer = useRotationPlayer({
    castFn: cast,
    loop: false,
  });

  const handleOverrideToggle = (key: keyof OverrideFlags) => {
    const next = !overrides[key];
    setOverrides((prev) => ({ ...prev, [key]: next }));
    applyOverrideLocal(localPlayer, key, next);
    switch (key) {
      case 'infiniteMana': sendChatCommand('/mana'); break;
      case 'noCooldowns': sendChatCommand('/cd'); break;
      case 'noGCD': sendChatCommand(next ? '/gcd 0' : '/gcd -1'); break;
      case 'noRangeGate': sendChatCommand('/norange'); break;
    }
  };

  const filteredAbilities = filter
    ? allAbilities.filter(
      (a) =>
        a.id.toLowerCase().includes(filter.toLowerCase()) ||
        (a.name ?? '').toLowerCase().includes(filter.toLowerCase()),
    )
    : allAbilities;

  const playRotation = (id: string) => {
    const r = rotations.find((rot) => rot.id === id);
    if (r) {
      setActiveRotationId(id);
      rotationPlayer.play(r);
    }
  };

  const startRecording = () => {
    setRecording({ name: 'New rotation', steps: [], startedAt: Date.now() });
  };

  const saveRecording = () => {
    if (!recording || recording.steps.length === 0) {
      setRecording(null);
      return;
    }
    const newRotation: RotationDefinition = {
      id: `rec-${Date.now()}`,
      name: recording.name || `Recorded ${new Date().toLocaleTimeString()}`,
      steps: recording.steps,
    };
    setRotations([...rotations, newRotation]);
    setRecording(null);
    setActiveRotationId(newRotation.id);
  };

  const deleteRotation = (id: string) => {
    setRotations(rotations.filter((r) => r.id !== id));
    if (activeRotationId === id) setActiveRotationId(null);
  };

  const activeOverrideCount = Object.values(overrides).filter(Boolean).length;
  const boundHotkeyCount = Object.keys(hotkeys).length;

  const rigTabs: TabDescriptor[] = [
    { id: 'bench', label: 'Cast Bench', icon: '✦' },
    { id: 'overrides', label: 'Overrides', icon: '⚡', badge: activeOverrideCount || undefined },
    { id: 'hotkeys', label: 'Hotkeys', icon: '⌨', badge: boundHotkeyCount || undefined },
    { id: 'rotations', label: 'Macros', icon: '↺', badge: recording ? recording.steps.length : undefined },
  ];

  return (
    <div style={styles.container}>
      <TabBar
        tabs={rigTabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as typeof activeTab)}
        variant="pills"
      />

      {activeTab === 'bench' && (
        <CastBenchPanel
          filter={filter}
          onFilterChange={setFilter}
          abilities={filteredAbilities}
          lastCastId={lastCastId}
          lastCastResult={lastCastResult}
          onCast={cast}
        />
      )}

      {activeTab === 'overrides' && (
        <OverridePanel
          overrides={overrides}
          onToggle={handleOverrideToggle}
        />
      )}

      {activeTab === 'hotkeys' && (
        <HotkeySlotsPanel
          abilities={allAbilities}
          hotkeys={hotkeys}
          onHotkeysChange={setHotkeys}
          onCast={cast}
        />
      )}

      {activeTab === 'rotations' && (
        <RotationMacrosPanel
          rotations={rotations}
          activeRotationId={activeRotationId}
          playerStatus={rotationPlayer.status}
          currentStep={rotationPlayer.currentStep}
          recordingStepCount={recording ? recording.steps.length : null}
          onStartRecording={startRecording}
          onSaveRecording={saveRecording}
          onCancelRecording={() => setRecording(null)}
          onStop={() => rotationPlayer.stop()}
          onPlay={playRotation}
          onPause={rotationPlayer.pause}
          onResume={rotationPlayer.resume}
          onDelete={deleteRotation}
        />
      )}
    </div>
  );
};
